import { Box, Button, Card, Chip, Stack, TextField, Typography } from '@mui/material'
import { Email, Payment, Person, Phone, Save, Cancel } from '@mui/icons-material'
import React from 'react'
import PropTypes from 'prop-types'

function AdminDataCard({ adminData, onEdit }) {
  const [editing, setEditing] = React.useState(false)
  const [form, setForm] = React.useState({ ...adminData })
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    setForm({ ...adminData })
  }, [adminData])

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const handleSave = async () => {
    setSaving(true)
    try {
      const result = await onEdit(form)
      if (result?.success) {
        setEditing(false)
      }
    } catch (e) {
      console.error('Failed to save admin data:', e)
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setForm({ ...adminData })
    setEditing(false)
  }

  return editing ? (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2, p: 2 }}>
      <Typography
        variant='subtitle1'
        fontWeight='bold'
        color='primary'
        sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        Informations de la réservation
      </Typography>
      <Stack spacing={1.5}>
        <TextField fullWidth label='Prénom' value={form.firstName || ''} onChange={handleChange('firstName')} size='small' />
        <TextField fullWidth label='Nom' value={form.lastName || ''} onChange={handleChange('lastName')} size='small' />
        <TextField fullWidth label='Email' type='email' value={form.email || ''} onChange={handleChange('email')} size='small' />
        <TextField fullWidth label='Téléphone' value={form.phone || ''} onChange={handleChange('phone')} size='small' />
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <Person sx={{ color: 'text.secondary', fontSize: 22 }} />
          <Typography variant='body2' color='text.secondary' sx={{ width: 100, flexShrink: 0 }}>
            Statut
          </Typography>
          <Stack direction='row' spacing={1}>
            <Chip
              label='Non payé'
              variant={form.paid === 'unpaid' ? 'outlined' : 'filled'}
              sx={{
                color: form.paid === 'unpaid' ? 'text.secondary' : 'text.primary',
                cursor: 'pointer',
                fontWeight: form.paid === 'unpaid' ? 'bold' : 'normal',
                '&:hover': { bgcolor: form.paid === 'unpaid' ? 'grey.200' : 'grey.400' }
              }}
              onClick={() => setForm((prev) => ({ ...prev, paid: 'unpaid' }))}
            />
            <Chip
              label='Payé'
              variant={form.paid === 'paid' ? 'outlined' : 'filled'}
              sx={{
                color: form.paid === 'paid' ? 'text.secondary' : 'text.primary',
                cursor: 'pointer',
                fontWeight: form.paid === 'paid' ? 'bold' : 'normal',
                '&:hover': { bgcolor: form.paid === 'paid' ? 'grey.200' : 'primary.main' }
              }}
              onClick={() => setForm((prev) => ({ ...prev, paid: 'paid' }))}
            />
          </Stack>
        </Stack>
        <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
          <Button variant='contained' color='primary' onClick={handleSave} disabled={saving} startIcon={saving ? null : <Save />}>
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
          <Button variant='outlined' onClick={handleCancel} startIcon={<Cancel />}>
            Annuler
          </Button>
        </Stack>
      </Stack>
    </Card>
  ) : (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        mb: 2,
        p: 2,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.06)' }
      }}
      onClick={() => setEditing(true)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
          Informations de la réservation
        </Typography>
        <Button size='small' onClick={() => setEditing(true)} sx={{ textTransform: 'none' }}>
          Modifier
        </Button>
      </Box>
      <InfoRow icon={Person} label='Client' value={`${adminData.firstName || ''} ${adminData.lastName || ''}`.trim() || '—'} />
      <InfoRow icon={Email} label='Email' value={adminData.email || '—'} />
      <InfoRow icon={Phone} label='Téléphone' value={adminData.phone || '—'} />
      <InfoRow
        icon={Payment}
        label='Statut'
        value={
          <Chip
            label={adminData.paid === 'paid' ? 'Payé' : 'Non payé'}
            color={adminData.paid === 'paid' ? 'success' : 'warning'}
            size='small'
            sx={{ ml: 0.5 }}
          />
        }
      />
    </Card>
  )
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 1 }}>
      <Icon sx={{ color: 'text.secondary', fontSize: 22 }} />
      <Typography variant='body2' color='text.secondary' sx={{ width: 100, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant='body1' fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  )
}

AdminDataCard.propTypes = {
  adminData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    paid: PropTypes.string
  }).isRequired,
  onEdit: PropTypes.func
}

InfoRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired
}

export default AdminDataCard
